/**
 * @file SubscriptionPanel
 * @description 「订阅管理」Tab 内容
 * 展示当前用户的会员订阅详情
 */

import {useMemo} from 'react';
import {EMPTY_ICON} from '../constants';
import {useSubscriptionDetail} from '../hooks/useSubscriptionDetail';
import styles from './index.module.less';
import type {CurrentSubscription} from '@/services/commerce/order/interface';
import Button from '@/components/common/Button';
import StatusView from '@/components/common/StatusView';
import {createCx} from '@/utils/cx';
import {formatUnixDate} from '@/utils/date';
import {fenToYuan} from '@/utils/money';
import {SubscriptionTypeValue} from '@/services/commerce/type';
import {openMembershipModal} from '@/commerce/modals/openModal';
import {MembershipModalSource} from '@/commerce/modals/MembershipModal/types';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';

const cx = createCx(styles);

interface RowProps {
    label: string;
    children: React.ReactNode;
    valueClassName?: string;
}

const Row: React.FC<RowProps> = ({label, children, valueClassName}) => (
    <div className={cx('row')}>
        <span className={cx('label')}>{label}</span>
        <span className={cx('value', valueClassName)}>{children}</span>
    </div>
);

interface DetailProps {
    subscription: CurrentSubscription;
}

const SubscriptionDetail: React.FC<DetailProps> = ({subscription}) => {
    const {
        levelName,
        subscriptionName,
        subscriptionType,
        startTime,
        payChannel,
        autoRenew,
        nextRenewTime,
        nextRenewAmount,
    } = subscription;

    // 非连续包月订阅不展示下次续费信息
    const isAutoMonthly = subscriptionType === SubscriptionTypeValue.AUTO_MONTHLY;
    const showNextRenewTime = isAutoMonthly && nextRenewTime !== null;
    const showNextRenewAmount = isAutoMonthly && nextRenewAmount !== null;

    return (
        <div className={cx('detail')}>
            <Row label="订阅类型">
                {levelName} · {subscriptionName}
            </Row>
            <Row label="生效时间">{formatUnixDate(startTime)}</Row>
            <Row label="支付方式">{payChannel}</Row>
            <Row label="续费状态">{autoRenew === 1 ? '自动续费中' : '未开启自动续费'}</Row>
            {showNextRenewTime && <Row label="下次续费时间">{formatUnixDate(nextRenewTime)}</Row>}
            {showNextRenewAmount && (
                <Row label="下次续费金额" valueClassName={cx('valueHighlight')}>
                    ¥{fenToYuan(nextRenewAmount)}
                </Row>
            )}
        </div>
    );
};

interface SubscriptionPanelProps {
    enabled: boolean;
}

type PanelStatus = 'loading' | 'error' | 'empty' | 'ready';

const SubscriptionPanel: React.FC<SubscriptionPanelProps> = ({enabled}) => {
    const {currentSubscription, isLoading, isError} = useSubscriptionDetail(enabled);
    const {sendClickLog} = useTracking();

    const status = useMemo<PanelStatus>(() => {
        if (isLoading) {
            return 'loading';
        }
        if (isError) {
            return 'error';
        }
        if (!currentSubscription) {
            return 'empty';
        }
        return 'ready';
    }, [currentSubscription, isError, isLoading]);

    if (status === 'empty') {
        return (
            <div className={cx('empty')}>
                <img className={cx('emptyIcon')} src={EMPTY_ICON} alt="" />
                <div className={cx('emptyText')}>当前无订阅</div>
                <Button
                    onClick={() => {
                        // 非会员点击【去订阅会员】埋点
                        sendClickLog({
                            page: TrackEvents.SUBSCRIPTION_MANAGEMENT_POP,
                            value: TrackEvents.SUBSCRIPTION_BTN,
                        });
                        openMembershipModal(
                            MembershipModalSource.SUBSCRIPTION_MANAGEMENT_ORDER_HISTORY_PAGE_MEMBERSHIP_BTN,
                        );
                    }}
                >
                    去订阅会员
                </Button>
            </div>
        );
    }

    if (status !== 'ready') {
        return (
            <div className={cx('placeholder')}>
                <StatusView status={status} size="medium" loadingTheme="white" />
            </div>
        );
    }

    if (!currentSubscription) {
        return null;
    }

    return <SubscriptionDetail subscription={currentSubscription} />;
};

export default SubscriptionPanel;
